import { useEffect, useState, useMemo } from "react";
import { GraduationCap, BookOpen, Award, Sparkles, MapPin, Calendar, Building2, Upload, X, Facebook, ArrowRight, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const LEVELS = [
  { k: "langue", t: "Année de langue", icon: BookOpen, d: "Programme intensif de chinois mandarin (1 an, HSK 1→5). Idéal pour les étudiants qui n'ont pas encore le niveau requis pour intégrer une licence.", duration: "1 an", price: "Dès 15 000 ¥/an" },
  { k: "licence", t: "Licence (Bachelor)", icon: GraduationCap, d: "Cursus de 3 à 4 ans en chinois ou en anglais. Filières : Business, Ingénierie, Médecine, Informatique, Relations Internationales.", duration: "3-4 ans", price: "Dès 18 000 ¥/an" },
  { k: "master", t: "Master", icon: Award, d: "Programmes de 2 à 3 ans avec spécialisation. Nombreuses bourses CSC & bourses provinciales disponibles.", duration: "2-3 ans", price: "Dès 25 000 ¥/an" },
  { k: "doctorat", t: "Doctorat (PhD)", icon: Sparkles, d: "3 à 4 ans de recherche encadrée dans les meilleures universités chinoises. Bourse totale possible (CSC).", duration: "3-4 ans", price: "Bourse CSC possible" },
];

const UNIVERSITIES = [
  { name: "Zhejiang Normal University", city: "Jinhua / Yiwu" },
  { name: "Beijing Language & Culture University", city: "Pékin" },
  { name: "Fudan University", city: "Shanghai" },
  { name: "Tsinghua University", city: "Pékin" },
  { name: "Sun Yat-sen University", city: "Guangzhou" },
  { name: "Jinan University", city: "Guangzhou" },
];

const FACEBOOK_URL = "https://www.facebook.com/share/1DStDgKxz8/?mibextid=wwXIfr";
const EMPTY_APP = {
  first_name: "", last_name: "", email: "", phone: "",
  country: "Sénégal", date_of_birth: "",
  current_level: "", target_level: "langue", field_of_study: "",
  preferred_city: "", academic_year: "", budget: "",
  scholarship_id: "", message: "", documents: [],
};

export default function Study() {
  const [scholarships, setScholarships] = useState([]);
  const [filterLevel, setFilterLevel] = useState("all");
  const [form, setForm] = useState(EMPTY_APP);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/scholarships").then((r) => setScholarships(r.data || [])).catch(() => {});
  }, []);

  const filtered = useMemo(() => (
    filterLevel === "all" ? scholarships : scholarships.filter((s) => s.level === filterLevel)
  ), [scholarships, filterLevel]);

  const onFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/student-applications/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setForm((prev) => ({ ...prev, documents: [...(prev.documents || []), r.data] }));
      }
    } catch (er) { setErr(formatApiError(er)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const removeDoc = (idx) => setForm((f) => ({ ...f, documents: f.documents.filter((_, i) => i !== idx) }));

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setSubmitting(true);
    try {
      const r = await api.post("/student-applications", form);
      setSuccess(r.data);
      setForm(EMPTY_APP);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (er) { setErr(formatApiError(er)); }
    finally { setSubmitting(false); }
  };

  const applyToScholarship = (s) => {
    setForm((f) => ({
      ...f,
      target_level: s.level || f.target_level,
      preferred_city: s.city || f.preferred_city,
      scholarship_id: s.id,
      field_of_study: s.field_of_study || f.field_of_study,
    }));
    document.getElementById("coif-apply-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-testid="coif-page">
      {/* Hero */}
      <section className="relative overflow-hidden bg-[#0A0F1A] text-white">
        <div className="absolute inset-0 opacity-30" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #FF6B00 0%, transparent 40%), radial-gradient(circle at 80% 70%, #DE2910 0%, transparent 40%)" }} />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <p className="kicker !text-[#FFCE00]" data-testid="coif-kicker">COIF · Étudier en Chine avec Negosino</p>
          <h1 className="font-display text-5xl lg:text-7xl font-black tracking-tighter mt-4 max-w-4xl">
            Votre <span className="text-[var(--accent)]">avenir académique</span><br />commence en Chine. 🇨🇳
          </h1>
          <p className="text-white/70 mt-6 max-w-2xl text-lg">
            Année de langue, licence, master ou doctorat — COIF vous accompagne de A à Z : choix de l'université, dossier de candidature, bourses d'études, visa, arrivée et installation sur place.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <a href="#coif-apply-form" className="btn-primary" data-testid="coif-hero-apply">
              Remplir le formulaire de candidature <ArrowRight size={18} />
            </a>
            <a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer" className="btn-outline !border-white !text-white hover:!bg-white hover:!text-black inline-flex items-center gap-2" data-testid="coif-hero-fb">
              <Facebook size={18} /> Suivez COIF sur Facebook
            </a>
          </div>
        </div>
      </section>

      {success && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-10">
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6 flex items-start gap-4" data-testid="coif-success">
            <CheckCircle2 className="text-emerald-600 shrink-0" />
            <div>
              <p className="font-display text-xl font-bold text-emerald-900">Candidature reçue ✓</p>
              <p className="text-emerald-800 text-sm mt-2">
                Votre référence : <strong>{success.reference}</strong>. Notre équipe COIF vous contactera sous 48h par WhatsApp au numéro <strong>{success.phone}</strong>.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* 4 cursus */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <p className="kicker">Les 4 cursus</p>
        <h2 className="font-display text-4xl lg:text-5xl font-black mt-4">Choisissez votre niveau d'études.</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
          {LEVELS.map((l, i) => {
            const Icon = l.icon;
            return (
              <div key={l.k} className="card-flat p-8 flex flex-col" data-testid={`coif-level-${l.k}`}>
                <div className="w-12 h-12 rounded-lg bg-[var(--accent)]/10 flex items-center justify-center">
                  <Icon className="text-[var(--accent)]" size={22} />
                </div>
                <h3 className="font-display text-xl font-bold mt-5">{l.t}</h3>
                <p className="text-[var(--ink-soft)] mt-3 text-sm leading-relaxed flex-1">{l.d}</p>
                <div className="mt-5 pt-5 border-t border-black/10 space-y-1 text-xs">
                  <p><span className="text-[var(--ink-soft)]">Durée :</span> <strong>{l.duration}</strong></p>
                  <p><span className="text-[var(--ink-soft)]">Frais :</span> <strong>{l.price}</strong></p>
                </div>
                <button onClick={() => { setForm((f) => ({ ...f, target_level: l.k })); document.getElementById("coif-apply-form")?.scrollIntoView({ behavior: "smooth" }); }} className="mt-5 text-sm font-bold text-[var(--accent)] inline-flex items-center gap-1 hover:gap-2 transition-all" data-testid={`coif-level-cta-${l.k}`}>
                  Postuler pour ce cursus <ArrowRight size={14} />
                </button>
              </div>
            );
          })}
        </div>
      </section>

      {/* Universités */}
      <section className="bg-[var(--bg-soft)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <p className="kicker">Universités partenaires</p>
          <h2 className="font-display text-4xl lg:text-5xl font-black mt-4">Un réseau solide à travers la Chine.</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-14" data-testid="coif-universities">
            {UNIVERSITIES.map((u) => (
              <div key={u.name} className="bg-white p-6 rounded-lg border border-black/5">
                <Building2 className="text-[var(--accent)]" size={20} />
                <p className="font-display font-bold mt-3">{u.name}</p>
                <p className="text-xs text-[var(--ink-soft)] mt-1 inline-flex items-center gap-1"><MapPin size={12} /> {u.city}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bourses & offres */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="kicker">Bourses & offres en cours</p>
            <h2 className="font-display text-4xl lg:text-5xl font-black mt-4">Postulez avant les deadlines.</h2>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="coif-filters">
            {["all", "langue", "licence", "master", "doctorat"].map((k) => (
              <button key={k} onClick={() => setFilterLevel(k)} className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider border ${filterLevel === k ? "bg-[var(--accent)] text-white border-[var(--accent)]" : "border-black/10 hover:border-[var(--accent)]"}`}>
                {k === "all" ? "Toutes" : k}
              </button>
            ))}
          </div>
        </div>
        {filtered.length === 0 ? (
          <div className="mt-14 text-center py-16 border border-dashed border-black/15 rounded-lg" data-testid="coif-no-scholarships">
            <p className="text-[var(--ink-soft)]">Aucune bourse publiée pour le moment. Revenez bientôt ou suivez notre page Facebook COIF pour être notifié.</p>
            <a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer" className="btn-outline mt-5 inline-flex items-center gap-2">
              <Facebook size={16} /> Suivre COIF sur Facebook
            </a>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
            {filtered.map((s) => (
              <article key={s.id} className="card-flat overflow-hidden flex flex-col" data-testid={`scholarship-${s.id}`}>
                {s.image_url && (
                  <div className="h-40 bg-black/5 overflow-hidden">
                    <img src={s.image_url} alt={s.title} className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex gap-2 flex-wrap">
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-[var(--accent)]/10 text-[var(--accent)]">{s.level}</span>
                    {s.amount && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-emerald-50 text-emerald-700">{s.amount}</span>}
                  </div>
                  <h3 className="font-display text-xl font-bold mt-4">{s.title}</h3>
                  <p className="text-sm text-[var(--ink-soft)] mt-2 line-clamp-3">{s.description}</p>
                  <div className="mt-4 space-y-1 text-xs text-[var(--ink-soft)]">
                    {s.university && <p className="inline-flex items-center gap-1"><Building2 size={12} /> {s.university}</p>}
                    {s.city && <p className="inline-flex items-center gap-1"><MapPin size={12} /> {s.city}</p>}
                    {s.deadline && <p className="inline-flex items-center gap-1"><Calendar size={12} /> Deadline : <strong>{s.deadline}</strong></p>}
                  </div>
                  <button onClick={() => applyToScholarship(s)} className="btn-primary mt-6 w-full !py-2 text-sm" data-testid={`apply-${s.id}`}>
                    Postuler
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {/* Formulaire */}
      <section className="bg-[#0A0F1A] text-white" id="coif-apply-form">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-24">
          <p className="kicker !text-[#FFCE00]">Formulaire de candidature</p>
          <h2 className="font-display text-4xl lg:text-5xl font-black mt-4">Démarrez votre projet d'études.</h2>
          <p className="text-white/60 mt-4 max-w-2xl">Remplissez ce formulaire et notre équipe COIF vous rappellera sur WhatsApp sous 48h pour préparer votre dossier.</p>

          {err && <div className="mt-6 bg-red-500/10 border border-red-500/30 text-red-200 p-3 rounded text-sm" data-testid="coif-error">{err}</div>}

          <form onSubmit={submit} className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="coif-form">
            <Field label="Prénom *" v={form.first_name} on={(v) => setForm({ ...form, first_name: v })} testid="coif-first-name" required />
            <Field label="Nom *" v={form.last_name} on={(v) => setForm({ ...form, last_name: v })} testid="coif-last-name" required />
            <Field label="Email *" type="email" v={form.email} on={(v) => setForm({ ...form, email: v })} testid="coif-email" required />
            <Field label="Téléphone WhatsApp *" v={form.phone} on={(v) => setForm({ ...form, phone: v })} testid="coif-phone" required placeholder="+221 77 000 00 00" />
            <Field label="Pays *" v={form.country} on={(v) => setForm({ ...form, country: v })} testid="coif-country" required />
            <Field label="Date de naissance" type="date" v={form.date_of_birth} on={(v) => setForm({ ...form, date_of_birth: v })} testid="coif-dob" />
            <Field label="Niveau actuel" v={form.current_level} on={(v) => setForm({ ...form, current_level: v })} testid="coif-current" placeholder="Ex: Baccalauréat, Licence 2..." />
            <Select label="Niveau visé *" v={form.target_level} on={(v) => setForm({ ...form, target_level: v })} testid="coif-target" options={[
              { v: "langue", t: "Année de langue" },
              { v: "licence", t: "Licence (Bachelor)" },
              { v: "master", t: "Master" },
              { v: "doctorat", t: "Doctorat (PhD)" },
            ]} />
            <Field label="Domaine d'études *" v={form.field_of_study} on={(v) => setForm({ ...form, field_of_study: v })} testid="coif-field" required placeholder="Ex: Informatique, Business, Médecine..." />
            <Field label="Ville chinoise préférée" v={form.preferred_city} on={(v) => setForm({ ...form, preferred_city: v })} testid="coif-city" placeholder="Ex: Shanghai, Pékin..." />
            <Field label="Année académique visée" v={form.academic_year} on={(v) => setForm({ ...form, academic_year: v })} testid="coif-year" placeholder="Ex: Septembre 2026" />
            <Field label="Budget approximatif" v={form.budget} on={(v) => setForm({ ...form, budget: v })} testid="coif-budget" placeholder="Ex: 5 000 € / an" />

            <div className="md:col-span-2">
              <label className="text-xs font-bold uppercase tracking-widest text-white/60">Documents (CV, diplômes, passeport)</label>
              <div className="mt-2 border-2 border-dashed border-white/20 rounded-lg p-6 text-center">
                <input type="file" id="coif-docs" multiple accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={onFile} className="hidden" data-testid="coif-file-input" />
                <label htmlFor="coif-docs" className="cursor-pointer inline-flex items-center gap-2 text-sm hover:text-[var(--accent)]">
                  <Upload size={16} /> {uploading ? "Envoi en cours…" : "Cliquer pour uploader (PDF ou image, max 10 Mo/fichier)"}
                </label>
              </div>
              {form.documents?.length > 0 && (
                <ul className="mt-3 space-y-2" data-testid="coif-docs-list">
                  {form.documents.map((d, i) => (
                    <li key={i} className="flex items-center justify-between bg-white/5 rounded px-3 py-2 text-sm">
                      <span className="truncate">📄 {d.name}</span>
                      <button type="button" onClick={() => removeDoc(i)} className="text-white/50 hover:text-red-400"><X size={14} /></button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="text-xs font-bold uppercase tracking-widest text-white/60">Message / motivations</label>
              <textarea rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 mt-2 focus:outline-none focus:border-[var(--accent)]"
                placeholder="Parlez-nous de votre projet, vos motivations..." data-testid="coif-message" />
            </div>

            <div className="md:col-span-2 flex justify-end pt-3">
              <button type="submit" disabled={submitting} className="btn-primary disabled:opacity-50" data-testid="coif-submit">
                {submitting ? "Envoi…" : "Envoyer ma candidature"}
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}

function Field({ label, v, on, type = "text", testid, required, placeholder }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-widest text-white/60">{label}</label>
      <input type={type} value={v} onChange={(e) => on(e.target.value)} required={required} placeholder={placeholder}
        data-testid={testid}
        className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 mt-2 focus:outline-none focus:border-[var(--accent)]" />
    </div>
  );
}

function Select({ label, v, on, options, testid }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-widest text-white/60">{label}</label>
      <select value={v} onChange={(e) => on(e.target.value)} data-testid={testid}
        className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 mt-2 focus:outline-none focus:border-[var(--accent)]">
        {options.map((o) => <option key={o.v} value={o.v} className="bg-[#0A0F1A]">{o.t}</option>)}
      </select>
    </div>
  );
}
